import { useRoles } from '@/hooks/useRoles';
import { getAllRoles } from '@/lib/roles';
import { useAppStore } from '@/stores/app';
import type { Org } from '@/types';
import ErrorMessage from '@components/ErrorMessage';
import Badge from '@nautilusbio/nb-design-system/Badge';
import { Button } from '@nautilusbio/nb-design-system/Button';
import Loading from '@nautilusbio/nb-design-system/Loading';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import styles from '../../styles.module.css';

import { CONTAINER_ID, POPOVER_CONFIG, POPOVER_POSITIONS } from './constants';
import { useCopyOrgId, useOrganizations, useOtherOrganizations, useRoleSwitcher } from './hooks';
import { OrgSelectorContent } from './OrgSelectorContent';
import { formatRoleLabel } from './utils';

interface OrgSelectorProps {
  selectedOrg: Org;
}

const allRolesArray = [...getAllRoles()];

/**
 * OrgSelector - Main component for selecting and switching organizations
 */
const OrgSelector: FC<OrgSelectorProps> = ({ selectedOrg }) => {
  const { t: tOrgs } = useTranslation('orgs');

  const { isOrgSelectorOpen, isSidebarCollapsed, setIsOrgSelectorOpen, setIsSidebarMenuOpen } = useAppStore(
    useShallow(state => ({
      isOrgSelectorOpen: state.isOrgSelectorOpen,
      isSidebarCollapsed: state.isSidebarCollapsed,
      setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
      setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
    })),
  );

  const { allowedRolesByOrg, assumedRolesByOrg, isGlobalSuperAdmin } = useRoles();

  const { data: organizations, isLoading, error } = useOrganizations();

  const { handleSwitchRole, isAssumingRole } = useRoleSwitcher(selectedOrg, () => setIsOrgSelectorOpen(false));
  const handleCopyOrgId = useCopyOrgId(selectedOrg.id, () => setIsOrgSelectorOpen(false));

  const otherOrgs = useOtherOrganizations(organizations, selectedOrg.id);

  const isSuperAdmin = useMemo(() => isGlobalSuperAdmin(), [isGlobalSuperAdmin]);

  const currentOrgAllowedRoles = useMemo(
    () => (isSuperAdmin ? allRolesArray : (allowedRolesByOrg[selectedOrg.id] ?? [])),
    [allowedRolesByOrg, selectedOrg.id, isSuperAdmin],
  );

  const currentRole = assumedRolesByOrg[selectedOrg.id];

  // For super admins, provide all roles for every org in the list
  const effectiveAllowedRolesByOrg = useMemo(() => {
    if (!isSuperAdmin) return allowedRolesByOrg;
    return Object.fromEntries([selectedOrg, ...otherOrgs].map(org => [org.id, allRolesArray]));
  }, [isSuperAdmin, allowedRolesByOrg, selectedOrg, otherOrgs]);

  const handleToggle = () => {
    setIsOrgSelectorOpen(!isOrgSelectorOpen);
    if (!isSidebarCollapsed) setIsSidebarMenuOpen(false);
  };

  const handleClose = () => {
    setIsOrgSelectorOpen(false);
    if (isSidebarCollapsed) setIsSidebarMenuOpen(false);
  };

  if (isLoading) {
    return <Loading className={styles.loading} label={tOrgs('org_loading')} type="inline" />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!organizations || organizations.length === 0 || !selectedOrg) {
    return null;
  }

  const Chevron = isOrgSelectorOpen ? ChevronUp : ChevronDown;

  const popoverContent = (
    <Stack
      className={clsx(styles.orgSelectorContainer, styles.popoverContainerWidth)}
      gap={1}
      id={CONTAINER_ID}
      orientation="vertical"
    >
      <OrgSelectorContent
        allowedRoles={currentOrgAllowedRoles}
        allowedRolesByOrg={effectiveAllowedRolesByOrg}
        assumedRolesByOrg={assumedRolesByOrg}
        currentRole={currentRole}
        isPending={isAssumingRole}
        onCopyOrgId={handleCopyOrgId}
        onRoleSwitch={handleSwitchRole}
        onSwitch={handleSwitchRole}
        otherOrgs={otherOrgs}
        selectedOrg={selectedOrg}
        tOrgs={tOrgs}
      />
    </Stack>
  );

  return (
    <Popover
      data-testid="org-selector-popover"
      content={popoverContent}
      isOpen={isOrgSelectorOpen}
      onClickOutside={handleClose}
      padding={POPOVER_CONFIG.padding}
      positions={isSidebarCollapsed ? ['right'] : POPOVER_POSITIONS}
      styled
    >
      <div>
        {isSidebarCollapsed ? (
          <Tooltip
            content={selectedOrg.name}
            hoverOptions={{ delay: 350, enabled: !isOrgSelectorOpen }}
            placement="right"
            trigger={
              <Button
                aria-expanded={isOrgSelectorOpen}
                aria-label={selectedOrg.name}
                data-testid="sidebar-org-selector-trigger"
                onPress={handleToggle}
                visual="unstyled"
              >
                {selectedOrg.name}
              </Button>
            }
          />
        ) : (
          <Button
            aria-expanded={isOrgSelectorOpen}
            aria-haspopup="listbox"
            className={styles.orgTriggerButton}
            data-testid="sidebar-org-selector-trigger"
            onPress={handleToggle}
            visual="unstyled"
          >
            <Stack
              alignItems="center"
              as="span"
              fullWidth
              gap={2}
              justifyContent="space-between"
              orientation="horizontal"
              className={styles.minWidthZero}
            >
              <Stack as="span" orientation="vertical" className={styles.minWidthZero}>
                <Text as="span" size="xs">
                  {selectedOrg.name}
                </Text>

                {currentRole && (
                  <Badge as="span" size="xs3" variant="info">
                    {formatRoleLabel(currentRole)}
                  </Badge>
                )}
              </Stack>
              <Chevron size={16} className={styles.chevronNoShrink} />
            </Stack>
          </Button>
        )}
      </div>
    </Popover>
  );
};

export default OrgSelector;
